import { useCallback, useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, X, Eye, EyeSlash, CurrencyEur } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useTableSort } from "../lib/useTableSort";
import { SortHeader } from "../components/SortHeader";
import AdvancesDialog from "../components/AdvancesDialog";
import AdminRolesManager from "../components/AdminRolesManager";
import { useRoles, roleLabel } from "../lib/roles";

const emptyForm = {
  id: null,
  name: "",
  email: "",
  dni: "",
  phone: "",
  password: "",
  role: "camarero",
  contract_hours_weekly: 40,
  hourly_rate: 10,
  active: true,
  show_breaks_in_history: false,
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [roles, reloadRoles] = useRoles();
  const ROLE_OPTIONS = [{ value: "admin", label: "Admin" }, ...roles.map((r) => ({ value: r.id, label: r.name }))];
  const { sortedRows: sortedUsers, sortKey, sortDir, toggleSort } = useTableSort(users, "name", "asc");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [advancesWorker, setAdvancesWorker] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setShowPassword(false);
    setShowForm(true);
  };

  const openEdit = (u) => {
    setForm({ ...u, password: u.password_plain || "", active: u.active !== false, show_breaks_in_history: !!u.show_breaks_in_history });
    setShowPassword(false);
    setShowForm(true);
  };

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        dni: form.dni,
        phone: form.phone,
        role: form.role,
        contract_hours_weekly: Number(form.contract_hours_weekly) || 0,
        hourly_rate: Number(form.hourly_rate) || 0,
        active: !!form.active,
        show_breaks_in_history: !!form.show_breaks_in_history,
      };
      if (form.password) payload.password = form.password;
      if (form.id) {
        await api.put(`/users/${form.id}`, payload);
        toast.success("Trabajador actualizado");
      } else {
        if (!form.password) {
          toast.error("La contraseña es obligatoria al crear");
          setSaving(false);
          return;
        }
        await api.post("/users", payload);
        toast.success("Trabajador creado");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = (u) => setConfirmDelete(u);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success(`Trabajador "${confirmDelete.name}" eliminado`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label-cap">gestión</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Trabajadores</h1>
        </div>
        <button
          onClick={openCreate}
          data-testid="add-worker-btn"
          className="btn-primary px-5 py-3 rounded-xl font-display font-bold flex items-center gap-2"
        >
          <Plus size={18} weight="bold" /> Nuevo trabajador
        </button>
      </div>

      <AdminRolesManager roles={roles} reload={reloadRoles} />

      <div className="card-flat overflow-hidden" data-testid="user-crud-table">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
              <th className="text-left p-4"><SortHeader sortKey="name" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Nombre</SortHeader></th>
              <th className="text-left p-4"><SortHeader sortKey="email" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Email</SortHeader></th>
              <th className="text-left p-4"><SortHeader sortKey="role" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Rol</SortHeader></th>
              <th className="text-center p-4"><SortHeader align="center" sortKey="active" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Activo</SortHeader></th>
              <th className="text-right p-4"><SortHeader align="right" sortKey="contract_hours_weekly" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>H/sem</SortHeader></th>
              <th className="text-right p-4"><SortHeader align="right" sortKey="hourly_rate" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>€/h</SortHeader></th>
              <th className="text-right p-4 label-cap">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-[var(--text-secondary)]">
                  No hay trabajadores. Crea el primero con el botón superior.
                </td>
              </tr>
            )}
            {sortedUsers.map((u) => (
              <tr
                key={u.id}
                className={`border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg)] ${
                  u.active === false ? "opacity-60" : ""
                }`}
              >
                <td className="p-4 font-display font-semibold">{u.name}</td>
                <td className="p-4 text-[var(--text-secondary)]">{u.email}</td>
                <td className="p-4">
                  <span className="px-2 py-1 rounded-full text-xs font-display font-semibold bg-[var(--bg)] border border-[var(--border)]">
                    {roleLabel(roles, u.role)}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <span
                    data-testid={`active-badge-${u.id}`}
                    className={`px-2 py-1 rounded-full text-xs font-display font-semibold ${
                      u.active === false
                        ? "bg-[var(--border)] text-[var(--text-secondary)]"
                        : "bg-[var(--primary)] text-white"
                    }`}
                  >
                    {u.active === false ? "Inactivo" : "Activo"}
                  </span>
                </td>
                <td className="p-4 text-right font-mono">{u.contract_hours_weekly}</td>
                <td className="p-4 text-right font-mono">{u.hourly_rate?.toFixed?.(2) ?? u.hourly_rate}</td>
                <td className="p-4">
                  <div className="flex justify-end gap-2">
                    {u.role !== "admin" && (
                      <button
                        onClick={() => setAdvancesWorker(u)}
                        data-testid={`advances-user-${u.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-white font-display text-sm font-semibold"
                        title="Adelantos"
                      >
                        <CurrencyEur size={14} weight="duotone" />
                        Adelantos
                      </button>
                    )}
                    <button
                      onClick={() => openEdit(u)}
                      data-testid={`edit-user-${u.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-white font-display text-sm font-semibold"
                    >
                      <PencilSimple size={14} weight="duotone" />
                      Editar
                    </button>
                    <button
                      onClick={() => remove(u)}
                      data-testid={`delete-user-${u.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-[var(--danger)] hover:text-white hover:border-[var(--danger)] font-display text-sm font-semibold"
                    >
                      <Trash size={14} weight="duotone" />
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-flat w-full max-w-2xl p-8 relative">
            <button
              onClick={() => setShowForm(false)}
              data-testid="close-user-form"
              className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[var(--border)] flex items-center justify-center"
            >
              <X size={18} />
            </button>
            <div className="label-cap mb-2">{form.id ? "editar" : "nuevo"}</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-6">
              {form.id ? "Editar trabajador" : "Nuevo trabajador"}
            </h2>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nombre" required>
                <input
                  required
                  value={form.name}
                  onChange={(e) => onChange("name", e.target.value)}
                  data-testid="form-name"
                  className="form-input"
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => onChange("email", e.target.value)}
                  data-testid="form-email"
                  className="form-input"
                />
              </Field>
              <Field label="DNI">
                <input
                  value={form.dni}
                  onChange={(e) => onChange("dni", e.target.value)}
                  data-testid="form-dni"
                  className="form-input"
                />
              </Field>
              <Field label="Teléfono">
                <input
                  value={form.phone}
                  onChange={(e) => onChange("phone", e.target.value)}
                  data-testid="form-phone"
                  className="form-input"
                />
              </Field>
              <Field label={form.id ? "Contraseña" : "Contraseña"} required={!form.id}>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => onChange("password", e.target.value)}
                    data-testid="form-password"
                    className="form-input pr-12"
                    placeholder={form.id ? "Editar contraseña actual" : ""}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    data-testid="toggle-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[var(--bg)] text-[var(--text-secondary)]"
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    {showPassword ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                  </button>
                </div>
                {form.id && (
                  <div className="text-xs text-[var(--text-secondary)] mt-1">
                    Pulsa el ojo para ver la contraseña actual. Modifícala si quieres cambiarla.
                  </div>
                )}
              </Field>
              <Field label="Rol">
                <select
                  value={form.role}
                  onChange={(e) => onChange("role", e.target.value)}
                  data-testid="form-role"
                  className="form-input"
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Horas de contrato (semanales)">
                <input
                  type="number"
                  step="0.5"
                  value={form.contract_hours_weekly}
                  onChange={(e) => onChange("contract_hours_weekly", e.target.value)}
                  data-testid="form-hours"
                  className="form-input"
                />
              </Field>
              <Field label="Precio por hora (€)">
                <input
                  type="number"
                  step="0.01"
                  value={form.hourly_rate}
                  onChange={(e) => onChange("hourly_rate", e.target.value)}
                  data-testid="form-rate"
                  className="form-input"
                />
              </Field>
              <div className="md:col-span-2">
                <label className="flex items-center gap-3 p-4 rounded-xl border border-[var(--border)] bg-[var(--bg)] cursor-pointer hover:bg-white transition">
                  <input
                    type="checkbox"
                    checked={!!form.active}
                    onChange={(e) => onChange("active", e.target.checked)}
                    data-testid="form-active"
                    className="w-5 h-5 accent-[var(--primary)] cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="font-display font-semibold">Trabajador activo</div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      Si está activo, aparece en la pantalla de fichaje. Si no, solo es visible aquí en administración.
                    </div>
                  </div>
                </label>
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-3 p-4 rounded-xl border border-[var(--border)] bg-[var(--bg)] cursor-pointer hover:bg-white transition">
                  <input
                    type="checkbox"
                    checked={!!form.show_breaks_in_history}
                    onChange={(e) => onChange("show_breaks_in_history", e.target.checked)}
                    data-testid="form-show-breaks"
                    className="w-5 h-5 accent-[var(--primary)] cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="font-display font-semibold">Ver tareas en “Mis horas”</div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      Si está activo, el trabajador ve el total y el listado de tareas en su vista personal. Si no, solo el administrador los ve.
                    </div>
                  </div>
                </label>
              </div>
              <div className="md:col-span-2 flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-5 py-3 rounded-xl border border-[var(--border)] font-display font-semibold hover:bg-[var(--bg)]"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="worker-form-submit"
                  className="btn-primary px-6 py-3 rounded-xl font-display font-bold disabled:opacity-50"
                >
                  {saving ? "Guardando…" : "Guardar"}
                </button>
              </div>
            </form>
            <style>{`.form-input { width: 100%; padding: 0.75rem 1rem; border: 1px solid var(--border); border-radius: 0.75rem; background: white; outline: none; }
              .form-input:focus { border-color: var(--primary); }`}</style>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-flat w-full max-w-md p-8" data-testid="confirm-delete-dialog">            <div className="label-cap mb-2 text-[var(--danger)]">eliminar trabajador</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-2">
              ¿Eliminar a {confirmDelete.name}?
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mb-6">
              Se borrarán también todos sus fichajes. Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                data-testid="confirm-delete-cancel"
                className="px-5 py-3 rounded-xl border border-[var(--border)] font-display font-semibold hover:bg-[var(--bg)] disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={deleting}
                data-testid="confirm-delete-ok"
                className="btn-danger px-6 py-3 rounded-xl font-display font-bold disabled:opacity-50"
              >
                {deleting ? "Eliminando…" : "Sí, eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {advancesWorker && (
        <AdvancesDialog worker={advancesWorker} onClose={() => { setAdvancesWorker(null); }} />
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="label-cap block mb-2">
        {label}
        {required && <span className="text-[var(--danger)] ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
